/**
 * 云存储服务类，用于与 Cloudflare Worker + KV 交互
 */
export class CloudStorageService {
    /**
     * 初始化方法，在类加载时调用
     */
    static initialize() {
        // 不需要在初始化时做任何事
    }
    /**
     * 检查是否已认证
     * @returns 是否已认证
     */
    static isAuth() {
        const cloudEnabled = localStorage.getItem(this.CLOUD_ENABLED_KEY);
        return cloudEnabled === 'true';
    }
    /**
     * 验证密码
     * @param password 要验证的密码
     * @returns 是否验证成功
     */
    static async authenticate(password) {
        try {
            // 直接比较硬编码的密码
            if (password === this.CLOUD_PASSWORD) {
                // 保存云存储开关状态到本地
                localStorage.setItem(this.CLOUD_ENABLED_KEY, 'true');
                return true;
            }
            // 清除云存储开关状态
            localStorage.removeItem(this.CLOUD_ENABLED_KEY);
            return false;
        }
        catch (error) {
            console.error('密码验证失败:', error);
            return false;
        }
    }
    /**
     * 清除认证状态
     */
    static clearAuthentication() {
        // 清除云存储开关状态
        localStorage.removeItem(this.CLOUD_ENABLED_KEY);
    }
    /**
     * 上传单个历史记录到云存储
     * @param item 历史记录项
     * @returns 上传是否成功
     */
    static async uploadHistoryItem(item) {
        if (!this.isAuth()) {
            throw new Error('未认证，请先输入密码');
        }
        try {
            console.log('上传历史记录项:', item);
            const response = await fetch(`${this.WORKER_URL}/api/history/item`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.CLOUD_PASSWORD}`,
                },
                body: JSON.stringify({ item }),
            });
            if (!response.ok) {
                throw new Error(`上传失败: ${response.status} ${response.statusText}`);
            }
            const result = await response.json();
            console.log('上传历史记录项结果:', result);
            return result.success;
        }
        catch (error) {
            console.error('上传历史记录项失败:', error);
            throw error;
        }
    }
    /**
     * 从云存储下载单个历史记录
     * @param id 历史记录ID
     * @returns 历史记录项
     */
    static async downloadHistoryItem(id) {
        if (!this.isAuth()) {
            throw new Error('未认证，请先输入密码');
        }
        try {
            const response = await fetch(`${this.WORKER_URL}/api/history/item/${id}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${this.CLOUD_PASSWORD}`,
                },
            });
            if (!response.ok) {
                throw new Error(`下载失败: ${response.status} ${response.statusText}`);
            }
            const result = await response.json();
            return result.item;
        }
        catch (error) {
            console.error('下载历史记录项失败:', error);
            throw error;
        }
    }
    /**
     * 从云存储删除单个历史记录
     * @param id 历史记录ID
     * @returns 删除是否成功
     */
    static async deleteHistoryItem(id) {
        if (!this.isAuth()) {
            throw new Error('未认证，请先输入密码');
        }
        try {
            const response = await fetch(`${this.WORKER_URL}/api/history/item/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${this.CLOUD_PASSWORD}`,
                },
            });
            if (!response.ok) {
                throw new Error(`删除失败: ${response.status} ${response.statusText}`);
            }
            const result = await response.json();
            return result.success;
        }
        catch (error) {
            console.error('删除历史记录项失败:', error);
            throw error;
        }
    }
    /**
     * 获取所有历史记录ID列表
     * @returns 历史记录ID数组
     */
    static async getHistoryIds() {
        if (!this.isAuth()) {
            throw new Error('未认证，请先输入密码');
        }
        try {
            console.log('获取历史记录ID列表...');
            const response = await fetch(`${this.WORKER_URL}/api/history/ids`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${this.CLOUD_PASSWORD}`,
                },
            });
            if (!response.ok) {
                throw new Error(`获取ID列表失败: ${response.status} ${response.statusText}`);
            }
            const result = await response.json();
            console.log('获取到的ID列表结果:', result);
            console.log('ID列表:', result.ids);
            return result.ids || [];
        }
        catch (error) {
            console.error('获取历史记录ID列表失败:', error);
            throw error;
        }
    }
    /**
     * 批量获取历史记录
     * @param ids 历史记录ID数组
     * @returns 历史记录数组
     */
    static async batchDownloadHistoryItems(ids) {
        if (!this.isAuth()) {
            throw new Error('未认证，请先输入密码');
        }
        try {
            console.log('批量下载历史记录，IDs:', ids);
            const response = await fetch(`${this.WORKER_URL}/api/history/batch`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.CLOUD_PASSWORD}`,
                },
                body: JSON.stringify({ ids }),
            });
            if (!response.ok) {
                throw new Error(`批量下载失败: ${response.status} ${response.statusText}`);
            }
            const result = await response.json();
            console.log('从云端获取的原始数据:', result);
            console.log('从云端获取的历史记录项:', result.items);
            return result.items || [];
        }
        catch (error) {
            console.error('批量下载历史记录失败:', error);
            throw error;
        }
    }
    /**
     * 清空所有历史记录
     * @returns 清空是否成功
     */
    static async clearAllHistory() {
        if (!this.isAuth()) {
            throw new Error('未认证，请先输入密码');
        }
        try {
            const response = await fetch(`${this.WORKER_URL}/api/history/clear`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${this.CLOUD_PASSWORD}`,
                },
            });
            if (!response.ok) {
                throw new Error(`清空失败: ${response.status} ${response.statusText}`);
            }
            const result = await response.json();
            return result.success;
        }
        catch (error) {
            console.error('清空历史记录失败:', error);
            throw error;
        }
    }
    /**
     * 检查网络连接状态
     * @returns 是否在线
     */
    static async isOnline() {
        try {
            await fetch(`${this.WORKER_URL}/api/health`, {
                method: 'GET',
                mode: 'no-cors',
                cache: 'no-cache'
            });
            return true;
        }
        catch {
            return false;
        }
    }
}
CloudStorageService.WORKER_URL = 'https://ziwei.sydf.cc'; // 需要替换为实际的 Worker URL
CloudStorageService.CLOUD_PASSWORD = 'ovo'; // 硬编码的云存储密码
CloudStorageService.CLOUD_ENABLED_KEY = 'iztro_cloud_enabled'; // 云存储开关状态键
